import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  useGetInstrumentSuggestion,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { useUserContext } from "@/contexts/userContext";
import { usePostCopyStrategy } from "@/api/strategyApi";
import { toast } from "../ui/use-toast";
import { useRouter } from "next/router";
import { randomString } from "@/helpers/helperFunctions";
import SymbolSelector from "../blocks/test/SymbolSelector/SymbolSelector";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { brokersList } from "@/components/blocks/connection/BrokerList";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

const SymbolUpdateModal = ({
  isOpen,
  setIsOpen,
  strategy,
  brokerSelectable = false,
}: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  strategy: any;
  brokerSelectable?: boolean;
}) => {
  const { t } = useTranslation("build-page");
  const { user, connectedBrokers, balanceLoading } = useUserContext();
  const { details } = useAppDetails();
  const router = useRouter();

  const [broker, setBroker] = useState<string>("");

  const [selectedSymbol, setSelectedSymbol] = useState<string[]>([]);
  const [instrumentInput, setInstrumentInput] = useState("");

  const [trendingSymbols, setTrendingSymbols] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [symbolData, setSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    data: trendingSymbolsData,
    isSuccess: isTrendingSymbolSuccess,
    isLoading: isTrendingSymbolLoading,
  } = useGetTrendingSymbols("forex");

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const {
    data: instrumentSuggestionData,
    isSuccess: isInstrumentSuggestionSuccess,
    isLoading: isInstrumentSuggestionLoading,
  } = useGetInstrumentSuggestion(user?.user_id, "equity");

  const copyHandler = async () => {
    if (!user?.user_id || !strategy?.keygen || !selectedSymbol?.[0]) return;
    setLoading(true);
    try {
      await copyStrategy({
        userId: user?.user_id,
        key: strategy?.keygen,
        symbol: selectedSymbol?.[0],
        broker_id: broker === "100" || broker === "3" ? "3" : broker,
      });
    } catch {
      toast({
        title: "Error copying strategy",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isTrendingSymbolSuccess && trendingSymbolsData) {
      setTrendingSymbols(
        Object.entries(
          trendingSymbolsData.data as { [key: string]: string },
        ).map(([symbol, name], index: number) => {
          return { name, symbol, short_name: symbol };
        }),
      );
    }
  }, [isTrendingSymbolSuccess, trendingSymbolsData, isTrendingSymbolLoading]);

  useEffect(() => {
    if (isInstrumentSuggestionSuccess && instrumentSuggestionData) {
      if (Array.isArray(instrumentSuggestionData.data)) {
        setSymbolData([...instrumentSuggestionData.data]);
      }
    }
  }, [
    isInstrumentSuggestionSuccess,
    instrumentSuggestionData,
    isInstrumentSuggestionLoading,
  ]);

  useEffect(() => {
    if (Object.keys(connectedBrokers).length === 0) {
      setBroker("3");
    } else if (details?.isWhiteLabled) {
      setBroker(details?.id);
    } else {
      setBroker(
        brokersList?.filter(
          (b) => connectedBrokers[Number(b.id)] && b.id !== "3",
        )?.[0]?.id || details?.id,
      );
    }
  }, [connectedBrokers, balanceLoading, details]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user?.user_id) {
      toast({
        title: "Strategy copied successfully",
        variant: "default",
      });
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(user?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            user.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`,
        );
        setLoading(false);
      }, 1000);
    }
  }, [isCopySuccess, isCopyPending]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="p-6  flex-col justify-between flex">
        <DialogHeader>
          <DialogTitle>
            <div className="text-sm">
              {`Copying strategy - ${strategy?.strategy_name}, ${strategy?.symbol}`}
            </div>
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-12">
            <div className="mt-5 flex flex-col gap-4">
              <h1 className="font-bold text-lg text-[#fff]">
                {"Choose Symbol"}
              </h1>
              <p>{`The current symbol - ${strategy?.symbol}, is not supported. Please choose a different symbol from the list to proceed with copying.`}</p>

              {brokerSelectable && !details?.isWhiteLabled && (
                <div className="flex flex-col gap-2">
                  {balanceLoading ? (
                    <Skeleton className="h-10 w-full" />
                  ) : (
                    <>
                      {Object.keys(connectedBrokers).length === 0 ? (
                        <div className="flex h-10 w-full items-center justify-between rounded-md border border-[--accent-primary] bg-background px-3 py-2 text-sm text-[--accent-primary]">
                          <div className="flex items-center gap-2">
                            <img
                              src={details?.brokerIcon}
                              alt="Explore"
                              className="h-[25px] aspect-square"
                            />
                            <p>{t("Explore with FREE market data")}</p>
                          </div>
                        </div>
                      ) : (
                        <Select
                          onValueChange={(e) => setBroker(e)}
                          value={broker}
                        >
                          <SelectTrigger className="text-[--accent-primary] border-[--accent-primary] focus:ring-0">
                            <SelectValue placeholder={t("Select Broker")} />
                          </SelectTrigger>
                          <SelectContent>
                            {brokersList
                              ?.filter(
                                (b) =>
                                  connectedBrokers[Number(b.id)] &&
                                  b.id !== "3",
                              )
                              .map((b) => (
                                <SelectItem
                                  key={b.id}
                                  value={b.id}
                                  className="flex items-center gap-2"
                                >
                                  <div className="flex items-center gap-2">
                                    <img
                                      className="h-[25px]"
                                      src={b.icon}
                                      alt={b.name ?? ""}
                                    />
                                    <p>{b.name}</p>
                                  </div>
                                </SelectItem>
                              ))}
                            <SelectItem
                              value="3"
                              className="flex items-center gap-2"
                            >
                              <div className="flex items-center gap-2">
                                <img
                                  src={details?.brokerIcon}
                                  alt="Explore"
                                  className="h-[25px] aspect-square"
                                />
                                <p>{t("Explore with FREE data")}</p>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </>
                  )}
                </div>
              )}

              <SymbolSelector
                broker={broker}
                isSingle
                selectedSymbols={selectedSymbol}
                onSelected={setSelectedSymbol}
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className="w-full flex mt-4 align-right justify-end">
            <div className="text-right flex gap-4">
              <Button variant={"secondary"} onClick={() => setIsOpen(false)}>
                {"Cancel"}
              </Button>
              <Button
                disabled={!selectedSymbol?.[0]}
                loadingText={loading}
                onClick={() => copyHandler()}
              >
                {"Copy strategy"}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SymbolUpdateModal;
